// The OT registers and reports.
//
// All read-only aggregates over the OT tables — there is no reporting write
// model and no snapshot, because a register that stores its own copy of the
// numbers is a register that can disagree with the records it summarises.
//
// Six of the nine are the requirement's registers (OT_0083 to OT_0088), which
// are almost entirely about money. The other three — cancellations, checklist
// compliance and theatre efficiency — were never requirements, and each is
// computed from data the module already captured.
//
// Every report runs over BOOKINGS in a date range, with the filters OT_0084
// names (surgeon, department, anaesthetist, surgery type, room) applied
// server-side and combining.

import React, { useCallback, useEffect, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import moment from "moment";

import {
  IPD_COLORS,
  IpdDataGrid,
  IpdDateField,
  IpdFormField,
  IpdSectionCard,
  IpdTextField,
  ipdCardSx,
} from "../../ipd/utils";
import { formatIstDateTime, formatIstTime } from "../../../utils/istDateTime";
import { fetchReport, fetchSetup, lookupImplants } from "../api/otApi";
import OtScreen from "../components/OtScreen";
import { OtSelect, errorMessage } from "../components/common";
import { REPORTS, SCHEDULING_TYPES } from "../constants";

export default function OtReports() {
  const [report, setReport] = useState("or-register");
  const [from, setFrom] = useState(moment().format("YYYY-MM-DD"));
  const [to, setTo] = useState(moment().format("YYYY-MM-DD"));
  const [roomId, setRoomId] = useState("");
  const [schedulingType, setSchedulingType] = useState("");
  const [rooms, setRooms] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setData(
        await fetchReport(report, {
          from,
          to,
          operating_room_id: roomId,
          scheduling_type: schedulingType,
          mode: "detailed",
        })
      );
    } catch (err) {
      setError(errorMessage(err, "Could not run the report"));
    } finally {
      setLoading(false);
    }
  }, [report, from, to, roomId, schedulingType]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    fetchSetup()
      .then((s) => setRooms(s.operating_rooms || []))
      .catch(() => setRooms([]));
  }, []);

  const meta = REPORTS.find((r) => r.id === report);

  return (
    <OtScreen
      title="OT Registers &amp; Reports"
      description={meta?.asks || ""}
      badge={data?.count}
      loading={loading}
      error={error}
      onRetry={load}
      actions={
        <Button size="small" variant="contained" startIcon={<PrintIcon />} onClick={() => window.print()}>
          Print
        </Button>
      }
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(5, 1fr)" },
          gap: 2,
          mb: 2.5,
        }}
      >
        <IpdFormField label="Report">
          <OtSelect
            value={report}
            onChange={(e) => setReport(e.target.value)}
            options={REPORTS.map((r) => ({ value: r.id, label: r.label }))}
          />
        </IpdFormField>
        <IpdFormField label="From">
          <IpdDateField value={from} onChange={(iso) => iso && setFrom(iso)} />
        </IpdFormField>
        <IpdFormField label="To">
          <IpdDateField value={to} onChange={(iso) => iso && setTo(iso)} />
        </IpdFormField>
        <IpdFormField label="Theatre">
          <OtSelect
            placeholder="All theatres"
            value={roomId}
            onChange={(e) => setRoomId(e.target.value)}
            options={rooms.map((r) => ({ value: r.id, label: r.name }))}
          />
        </IpdFormField>
        <IpdFormField label="Scheduling type">
          <OtSelect
            placeholder="All types"
            value={schedulingType}
            onChange={(e) => setSchedulingType(e.target.value)}
            options={SCHEDULING_TYPES}
          />
        </IpdFormField>
      </Box>

      <IpdSectionCard
        title={meta?.label}
        subtitle={`${data?.from || from} to ${data?.to || to} · ${
          data?.count ?? 0
        } theatre slot(s) in range`}
      >
        <ReportBody report={report} data={data} />
      </IpdSectionCard>

      <Box sx={{ mt: 2.5 }}>
        <ImplantLookupCard />
      </Box>
    </OtScreen>
  );
}


/**
 * A headline figure with its own words underneath.
 *
 * `value` is null when the question could not be answered — no cases in the
 * range, nothing measurable — and that renders as an em dash rather than a
 * zero. A zero per cent compliance rate and a rate nobody could compute are
 * very different findings, and a dashboard that shows both as 0 is worse than
 * useless.
 */
function Stat({ label, value, suffix, tone, hint }) {
  const unknown = value === null || value === undefined;
  return (
    <Box
      sx={{
        ...ipdCardSx,
        p: 2.5,
        minWidth: 168,
        flex: "1 1 168px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      {/* Label and value metrics are IpdStatCard's, so a report tile and a
          dashboard tile are visibly the same object. It is not IpdStatCard
          itself because these carry a hint \u2014 "2 of 12 booked" \u2014 and that
          component has nowhere to put one. */}
      <Typography
        sx={{
          fontSize: 11,
          color: IPD_COLORS.textSecondary,
          textTransform: "uppercase",
          letterSpacing: 0.8,
          fontWeight: 600,
          lineHeight: 1.3,
          mb: 1,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{ fontSize: 32, fontWeight: 700, lineHeight: 1, color: tone || IPD_COLORS.textPrimary }}
      >
        {unknown ? "\u2014" : `${value}${suffix || ""}`}
      </Typography>
      {hint && (
        <Typography sx={{ mt: 0.75, fontSize: 11.5, color: IPD_COLORS.textMuted }}>
          {hint}
        </Typography>
      )}
    </Box>
  );
}

function StatRow({ children }) {
  return (
    <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 2.5 }}>{children}</Box>
  );
}

/**
 * The recall query: which patients received a given device.
 *
 * The whole reason the device identifier is captured at all. A recall names a
 * model and often a lot; without this the answer is a manual read of every
 * operation note in the range, which in practice means the question does not get
 * asked. Deliberately not bounded by the report's date window — a recall reaches
 * back as far as the device was implanted.
 */
function ImplantLookupCard() {
  const [deviceIdentifier, setDeviceIdentifier] = useState("");
  const [batchNo, setBatchNo] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const search = async () => {
    setBusy(true);
    setError("");
    try {
      setResult(
        await lookupImplants({ device_identifier: deviceIdentifier, batch_no: batchNo })
      );
    } catch (err) {
      setResult(null);
      setError(errorMessage(err, "Could not run the lookup"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <IpdSectionCard
      title="Implant recall lookup"
      subtitle="Which patients received a given device. The dates above do not apply."
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr auto" },
          gap: 2,
          alignItems: "end",
          mb: 2,
        }}
      >
        <IpdFormField label="Device identifier">
          <IpdTextField
            fullWidth
            placeholder="The (01) of a scanned UDI"
            value={deviceIdentifier}
            onChange={(e) => setDeviceIdentifier(e.target.value)}
          />
        </IpdFormField>
        <IpdFormField label="Batch / lot">
          <IpdTextField
            fullWidth
            value={batchNo}
            onChange={(e) => setBatchNo(e.target.value)}
          />
        </IpdFormField>
        <Button
          variant="contained"
          disabled={busy || (!deviceIdentifier.trim() && !batchNo.trim())}
          onClick={search}
        >
          Find patients
        </Button>
      </Box>

      {error && (
        <Typography sx={{ fontSize: 13, color: IPD_COLORS.error.text }}>{error}</Typography>
      )}

      {result && (
        <>
          <Typography sx={{ fontSize: 13.5, fontWeight: 700, mb: 1 }}>
            {result.count} patient(s)
          </Typography>
          <Grid
            empty="No patient in this facility received that device."
            rows={(result.implants || []).map((r) => ({ ...r, id: r.consumption_id }))}
            columns={[
              { field: "patient_name", headerName: "Patient", flex: 1, minWidth: 160 },
              { field: "health_record_id", headerName: "MRN", width: 130 },
              { field: "case_number", headerName: "Case", width: 130 },
              { field: "item_name", headerName: "Device", flex: 1, minWidth: 160 },
              { field: "batch_no", headerName: "Batch", width: 120 },
              { field: "serial_no", headerName: "Serial", width: 130 },
              {
                field: "implanted_at",
                headerName: "Implanted",
                width: 160,
                valueGetter: (p) => formatIstDateTime(p.row.implanted_at),
              },
              { field: "surgeon_name", headerName: "Surgeon", width: 150 },
            ]}
          />
        </>
      )}
    </IpdSectionCard>
  );
}

function Grid({ rows, columns, empty }) {
  if (!rows || rows.length === 0) {
    return <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 13.5 }}>{empty}</Typography>;
  }
  return (
    <IpdDataGrid
      rows={rows.map((r, i) => ({ id: r.id ?? r.ot_case_id ?? `${i}`, ...r }))}
      columns={columns}
      getRowId={(row) => row.id}
      embedded
      pageSize={25}
    />
  );
}

function ReportBody({ report, data }) {
  if (!data) return null;

  if (report === "or-register") {
    return (
      <Grid
        empty="Nothing in theatre over this range."
        rows={data.rows}
        columns={[
          { field: "case_number", headerName: "Case", width: 130 },
          { field: "patient_name", headerName: "Patient", flex: 1, minWidth: 150 },
          { field: "operating_room_name", headerName: "Theatre", width: 120 },
          {
            field: "scheduled_start",
            headerName: "Scheduled",
            width: 150,
            valueGetter: (p) => formatIstDateTime(p.row.scheduled_start),
          },
          {
            field: "in_or_at",
            headerName: "In room",
            width: 100,
            valueGetter: (p) => formatIstTime(p.row.in_or_at),
          },
          {
            field: "incision_at",
            headerName: "Incision",
            width: 100,
            valueGetter: (p) => formatIstTime(p.row.incision_at),
          },
          {
            field: "out_of_or_at",
            headerName: "Out",
            width: 100,
            valueGetter: (p) => formatIstTime(p.row.out_of_or_at),
          },
          { field: "minutes_in_or", headerName: "In OR (min)", width: 110 },
          { field: "minutes_in_pacu", headerName: "In PACU (min)", width: 120 },
          { field: "primary_surgeon_name", headerName: "Surgeon", width: 150 },
          { field: "reschedule_count", headerName: "Moved", width: 90 },
        ]}
      />
    );
  }

  if (report === "surgery-bill") {
    return (
      <>
        <Typography sx={{ fontSize: 15, fontWeight: 700, mb: 1.5 }}>
          Grand total: {data.grand_total}
        </Typography>
        <Typography sx={{ fontSize: 12.5, fontWeight: 700, mt: 2, mb: 1 }}>By surgeon</Typography>
        <Grid
          empty="Nothing billed over this range."
          rows={(data.by_surgeon || []).map((r, i) => ({ ...r, id: `s-${i}` }))}
          columns={[
            { field: "surgeon_name", headerName: "Surgeon", flex: 1, minWidth: 180 },
            { field: "cases", headerName: "Cases", width: 90 },
            { field: "charges_gross", headerName: "Charges", width: 120 },
            { field: "discount", headerName: "Discount", width: 110 },
            { field: "consumables", headerName: "Consumables", width: 130 },
            { field: "total", headerName: "Total", width: 120 },
          ]}
        />
        <Typography sx={{ fontSize: 12.5, fontWeight: 700, mt: 3, mb: 1 }}>Per case</Typography>
        <Grid
          empty="No cases."
          rows={data.rows}
          columns={[
            { field: "case_number", headerName: "Case", width: 130 },
            { field: "patient_name", headerName: "Patient", flex: 1, minWidth: 150 },
            { field: "procedure_name", headerName: "Procedure", flex: 1, minWidth: 160 },
            { field: "primary_surgeon_name", headerName: "Surgeon", width: 150 },
            { field: "charges_gross", headerName: "Charges", width: 110 },
            { field: "discount", headerName: "Discount", width: 110 },
            { field: "consumables", headerName: "Consumables", width: 130 },
            { field: "total", headerName: "Total", width: 110 },
          ]}
        />
      </>
    );
  }

  if (report === "billed-unbilled") {
    return (
      <>
        <Typography sx={{ fontSize: 13.5, fontWeight: 700, mb: 1 }}>
          Unbilled ({data.unbilled_count}) — the actionable half
        </Typography>
        <Grid
          empty="Everything is billed."
          rows={data.unbilled}
          columns={[
            { field: "case_number", headerName: "Case", width: 130 },
            { field: "patient_name", headerName: "Patient", flex: 1, minWidth: 150 },
            { field: "procedure_name", headerName: "Procedure", flex: 1, minWidth: 150 },
            { field: "operating_room_name", headerName: "Theatre", width: 120 },
            { field: "lifecycle_status", headerName: "Status", width: 130 },
            { field: "charge_lines", headerName: "Lines", width: 90 },
            { field: "posted_lines", headerName: "Posted", width: 90 },
            {
              field: "has_admission",
              headerName: "Billable",
              width: 100,
              valueGetter: (p) => (p.row.has_admission ? "yes" : "no admission"),
            },
          ]}
        />
        <Typography sx={{ fontSize: 13.5, fontWeight: 700, mt: 3, mb: 1 }}>
          Billed ({data.billed_count})
        </Typography>
        <Grid
          empty="Nothing billed yet."
          rows={data.billed}
          columns={[
            { field: "case_number", headerName: "Case", width: 130 },
            { field: "patient_name", headerName: "Patient", flex: 1, minWidth: 150 },
            { field: "procedure_name", headerName: "Procedure", flex: 1, minWidth: 150 },
            { field: "charge_lines", headerName: "Lines", width: 90 },
          ]}
        />
      </>
    );
  }

  if (report === "procedure-time") {
    return (
      <>
        <Grid
          empty="No procedures over this range."
          rows={data.rows}
          columns={[
            { field: "case_number", headerName: "Case", width: 130 },
            { field: "procedure_name", headerName: "Procedure", flex: 1, minWidth: 180 },
            { field: "primary_surgeon_name", headerName: "Surgeon", width: 150 },
            { field: "scheduled_minutes", headerName: "Booked", width: 100 },
            { field: "estimated_minutes", headerName: "Estimated", width: 110 },
            { field: "actual_minutes", headerName: "Actual", width: 100 },
            {
              field: "variance_minutes",
              headerName: "Variance",
              width: 110,
              renderCell: (p) =>
                p.row.variance_minutes === null ? (
                  "—"
                ) : (
                  <span style={{ color: p.row.variance_minutes > 0 ? IPD_COLORS.error.text : IPD_COLORS.success.text }}>
                    {p.row.variance_minutes > 0 ? "+" : ""}
                    {p.row.variance_minutes}
                  </span>
                ),
            },
          ]}
        />
        <Typography sx={{ fontSize: 12.5, fontWeight: 700, mt: 3, mb: 1 }}>
          Average by procedure
        </Typography>
        <Grid
          empty="Nothing completed yet."
          rows={(data.by_procedure || []).map((r, i) => ({ ...r, id: `p-${i}` }))}
          columns={[
            { field: "procedure_name", headerName: "Procedure", flex: 1, minWidth: 200 },
            { field: "cases", headerName: "Cases", width: 90 },
            { field: "total_minutes", headerName: "Total minutes", width: 130 },
            { field: "average_minutes", headerName: "Average", width: 110 },
          ]}
        />
      </>
    );
  }

  if (report === "procedure-numeric") {
    return (
      <Grid
        empty="No procedures over this range."
        rows={(data.rows || []).map((r, i) => ({ ...r, id: `n-${i}` }))}
        columns={[
          { field: "procedure_name", headerName: "Procedure", flex: 1, minWidth: 200 },
          { field: "cases", headerName: "Cases", width: 90 },
          { field: "charges_gross", headerName: "Charges", width: 120 },
          { field: "discount", headerName: "Discount", width: 110 },
          { field: "consumables", headerName: "Consumables", width: 130 },
          { field: "net", headerName: "Net", width: 120 },
          { field: "average_per_case", headerName: "Average / case", width: 140 },
        ]}
      />
    );
  }

  if (report === "cancellations") {
    return (
      <>
        <StatRow>
          <Stat
            label="Did not happen"
            value={data.cancellation_rate_pct}
            suffix="%"
            tone={IPD_COLORS.error.text}
            hint={`${data.not_performed} of ${data.booked_cases} booked`}
          />
          <Stat
            label="Cancelled on the day"
            value={data.same_day_rate_pct}
            suffix="%"
            tone={IPD_COLORS.warning.text}
            hint="the expensive kind — the slot is wasted"
          />
          <Stat
            label="Judged avoidable"
            value={data.avoidable_share_pct}
            suffix="%"
            hint={`${data.avoidable} of the cancellations`}
          />
        </StatRow>

        <Typography sx={{ fontSize: 12.5, fontWeight: 700, mb: 1 }}>By category</Typography>
        <Grid
          empty="Every booked case went ahead."
          rows={(data.by_category || []).map((r, i) => ({ ...r, id: `c-${i}` }))}
          columns={[
            { field: "label", headerName: "Category", flex: 1, minWidth: 240 },
            { field: "count", headerName: "Cases", width: 90 },
            { field: "same_day", headerName: "On the day", width: 120 },
            { field: "avoidable", headerName: "Avoidable", width: 110 },
          ]}
        />

        <Typography sx={{ fontSize: 12.5, fontWeight: 700, mt: 3, mb: 1 }}>Each case</Typography>
        <Grid
          empty="Nothing cancelled or postponed over this range."
          rows={data.rows}
          columns={[
            { field: "case_number", headerName: "Case", width: 130 },
            { field: "patient_name", headerName: "Patient", flex: 1, minWidth: 150 },
            {
              field: "scheduled_for",
              headerName: "Was booked for",
              width: 160,
              valueGetter: (p) => formatIstDateTime(p.row.scheduled_for),
            },
            { field: "outcome", headerName: "Outcome", width: 120 },
            { field: "category_label", headerName: "Category", flex: 1, minWidth: 200 },
            {
              field: "same_day",
              headerName: "On the day",
              width: 110,
              valueGetter: (p) => (p.row.same_day ? "yes" : "no"),
            },
            {
              field: "avoidable",
              headerName: "Avoidable",
              width: 110,
              valueGetter: (p) =>
                p.row.avoidable === null || p.row.avoidable === undefined
                  ? "not judged"
                  : p.row.avoidable
                  ? "yes"
                  : "no",
            },
            { field: "reason", headerName: "Reason", flex: 1, minWidth: 200 },
          ]}
        />
      </>
    );
  }

  if (report === "checklist-compliance") {
    const p = data.prophylaxis || {};
    return (
      <>
        <StatRow>
          <Stat
            label="All three sections"
            value={data.compliance_rate_pct}
            suffix="%"
            tone={data.compliance_rate_pct === 100 ? IPD_COLORS.success.text : IPD_COLORS.error.text}
            hint={`${data.all_three_complete} of ${data.cases_reaching_theatre} that reached theatre`}
          />
          <Stat
            label="Antibiotic on time"
            value={p.on_time_rate_pct}
            suffix="%"
            hint={`within ${p.window_minutes} min before incision`}
          />
          <Stat
            label="Not measurable"
            value={(p.unmeasurable || 0) + (p.none_flagged || 0)}
            hint="no dose flagged, or a missing timestamp"
          />
        </StatRow>

        <Typography sx={{ fontSize: 12.5, fontWeight: 700, mb: 1 }}>By section</Typography>
        <Grid
          empty="No case reached theatre over this range."
          rows={(data.by_section || []).map((r) => ({ ...r, id: r.section }))}
          columns={[
            { field: "section", headerName: "Section", flex: 1, minWidth: 160 },
            { field: "completed", headerName: "Completed", width: 120 },
            {
              field: "rate_pct",
              headerName: "Rate",
              width: 100,
              valueGetter: (p2) => (p2.row.rate_pct === null ? "—" : `${p2.row.rate_pct}%`),
            },
          ]}
        />

        <Typography sx={{ fontSize: 12.5, fontWeight: 700, mt: 3, mb: 1 }}>Each case</Typography>
        <Grid
          empty="No case reached theatre over this range."
          rows={data.rows}
          columns={[
            { field: "case_number", headerName: "Case", width: 130 },
            { field: "patient_name", headerName: "Patient", flex: 1, minWidth: 150 },
            { field: "primary_surgeon_name", headerName: "Surgeon", width: 150 },
            {
              field: "sign_in",
              headerName: "Sign In",
              width: 90,
              valueGetter: (p2) => (p2.row.sections?.SIGN_IN ? "yes" : "NO"),
            },
            {
              field: "time_out",
              headerName: "Time Out",
              width: 100,
              valueGetter: (p2) => (p2.row.sections?.TIME_OUT ? "yes" : "NO"),
            },
            {
              field: "sign_out",
              headerName: "Sign Out",
              width: 100,
              valueGetter: (p2) => (p2.row.sections?.SIGN_OUT ? "yes" : "NO"),
            },
            {
              field: "fire_risk_high",
              headerName: "Fire risk",
              width: 100,
              valueGetter: (p2) => (p2.row.fire_risk_high ? "HIGH" : ""),
            },
            { field: "prophylaxis_drug", headerName: "Antibiotic", width: 140 },
            {
              field: "prophylaxis_minutes_before_incision",
              headerName: "Min before incision",
              width: 160,
              renderCell: (p2) =>
                p2.row.prophylaxis_minutes_before_incision === null ? (
                  "—"
                ) : (
                  <span
                    style={{
                      color: p2.row.prophylaxis_state === "ON_TIME" ? IPD_COLORS.success.text : IPD_COLORS.error.text,
                    }}
                  >
                    {p2.row.prophylaxis_minutes_before_incision}
                  </span>
                ),
            },
            { field: "prophylaxis_state", headerName: "Timing", width: 140 },
          ]}
        />
      </>
    );
  }

  if (report === "or-efficiency") {
    const f = data.fcots || {};
    const t = data.turnover || {};
    return (
      <>
        <StatRow>
          <Stat
            label="First case on time"
            value={f.rate_pct}
            suffix="%"
            tone={f.rate_pct !== null && f.rate_pct < 70 ? IPD_COLORS.error.text : IPD_COLORS.success.text}
            hint={`${f.on_time} of ${f.measured} measured`}
          />
          <Stat
            label="Average first-case delay"
            value={f.average_delay_minutes}
            suffix=" min"
            hint="a late first case pushes every case behind it"
          />
          <Stat
            label="Average turnover"
            value={t.average_minutes}
            suffix=" min"
            hint={`${t.count} changeover(s), gaps over ${t.outlier_threshold_minutes} min excluded`}
          />
        </StatRow>

        <Typography sx={{ fontSize: 12.5, fontWeight: 700, mb: 1 }}>
          First case of each theatre, each day
        </Typography>
        <Grid
          empty="Nothing booked over this range."
          rows={(data.first_cases || []).map((r, i) => ({ ...r, id: `f-${i}` }))}
          columns={[
            { field: "date", headerName: "Date", width: 110 },
            { field: "operating_room_name", headerName: "Theatre", width: 130 },
            { field: "case_number", headerName: "Case", width: 130 },
            { field: "patient_name", headerName: "Patient", flex: 1, minWidth: 150 },
            {
              field: "scheduled_start",
              headerName: "Booked for",
              width: 110,
              valueGetter: (p2) => formatIstTime(p2.row.scheduled_start),
            },
            {
              field: "actual_in_or",
              headerName: "In room",
              width: 100,
              valueGetter: (p2) => formatIstTime(p2.row.actual_in_or),
            },
            {
              field: "delay_minutes",
              headerName: "Late by",
              width: 110,
              renderCell: (p2) =>
                p2.row.delay_minutes === null ? (
                  "—"
                ) : (
                  <span style={{ color: p2.row.on_time ? IPD_COLORS.success.text : IPD_COLORS.error.text }}>
                    {p2.row.delay_minutes > 0 ? `+${p2.row.delay_minutes}` : p2.row.delay_minutes}
                  </span>
                ),
            },
          ]}
        />

        <Typography sx={{ fontSize: 12.5, fontWeight: 700, mt: 3, mb: 1 }}>
          Turnover between cases
        </Typography>
        <Grid
          empty="No back-to-back cases to measure."
          rows={(data.turnovers || []).map((r, i) => ({ ...r, id: `t-${i}` }))}
          columns={[
            { field: "date", headerName: "Date", width: 110 },
            { field: "operating_room_name", headerName: "Theatre", width: 130 },
            { field: "from_case", headerName: "Out of", width: 130 },
            { field: "to_case", headerName: "In to", width: 130 },
            { field: "minutes", headerName: "Minutes", width: 110 },
          ]}
        />

        <Typography sx={{ fontSize: 12.5, fontWeight: 700, mt: 3, mb: 1 }}>
          Theatre use — in-room minutes against the minutes booked
        </Typography>
        <Grid
          empty="Nothing booked over this range."
          rows={(data.by_room || []).map((r, i) => ({ ...r, id: `r-${i}` }))}
          columns={[
            { field: "operating_room_name", headerName: "Theatre", flex: 1, minWidth: 160 },
            { field: "cases", headerName: "Cases", width: 90 },
            { field: "booked_minutes", headerName: "Booked", width: 110 },
            { field: "in_or_minutes", headerName: "In room", width: 110 },
            {
              field: "utilisation_pct",
              headerName: "Use",
              width: 100,
              valueGetter: (p2) =>
                p2.row.utilisation_pct === null ? "—" : `${p2.row.utilisation_pct}%`,
            },
          ]}
        />
      </>
    );
  }

  if (report === "surgeon-wise") {
    return (
      <Grid
        empty="No cases over this range."
        rows={(data.rows || []).map((r, i) => ({ ...r, id: `w-${i}` }))}
        columns={[
          { field: "surgeon_name", headerName: "Surgeon", flex: 1, minWidth: 180 },
          { field: "scheduled", headerName: "Scheduled", width: 110 },
          { field: "emergency", headerName: "Emergency", width: 110 },
          { field: "completed", headerName: "Completed", width: 110 },
          { field: "cancelled", headerName: "Cancelled", width: 110 },
          { field: "postponed", headerName: "Postponed", width: 110 },
          { field: "total_minutes", headerName: "Theatre minutes", width: 140 },
        ]}
      />
    );
  }

  return null;
}
